import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, Clock, ArrowLeft, ArrowRight, Search, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { blogPosts, categories, isNewPost, BlogPost as BlogPostType, getFallbackImageUrl } from "@/data/blogPosts";
import { blogPostsEn, categoryMapHeToEn } from "@/data/blogPostsEn";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

export default function Blog() {
  const { t, i18n } = useTranslation('blog');
  const isRTL = i18n.language === 'he';
  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;
  
  const allCategory = isRTL ? "הכל" : "All";
  const [selectedCategory, setSelectedCategory] = useState(allCategory);
  const [searchQuery, setSearchQuery] = useState("");
  const [visiblePosts, setVisiblePosts] = useState(9);

  // When switching languages, reset category to the correct language label
  useEffect(() => {
    setSelectedCategory(isRTL ? "הכל" : "All");
  }, [isRTL]);

  // Get translated categories
  const translatedCategories = useMemo(() => {
    if (isRTL) {
      return categories;
    }
    // For English, derive from the shared category map (single source of truth)
    return categories.map((cat) => {
      if (cat === "הכל") return "All";
      return categoryMapHeToEn[cat] || cat;
    });
  }, [isRTL]);

  // Get translated post data (must be defined before filteredPosts useMemo)
  const getTranslatedPost = (post: BlogPostType) => {
    if (isRTL) return post;
    const enTranslation = blogPostsEn[post.slug];
    if (enTranslation) {
      return {
        ...post,
        title: enTranslation.title,
        excerpt: enTranslation.excerpt,
        category: enTranslation.category,
        readTime: enTranslation.readTime,
        imageAlt: enTranslation.imageAlt,
        keywords: enTranslation.keywords
      };
    }
    // Fallback: keep the UI fully English even if a post translation is missing
    return {
      ...post,
      title: "Article coming soon (English)",
      excerpt: "This article is not yet available in English.",
      category: categoryMapHeToEn[post.category] || "General",
      readTime: "",
      imageAlt: "Workplace safety article",
      keywords: ["workplace safety"]
    };
  };

  // Get translated category for display
  const getDisplayCategory = (hebrewCategory: string): string => {
    if (isRTL) return hebrewCategory;
    return categoryMapHeToEn[hebrewCategory] || hebrewCategory;
  };

  const filteredPosts = useMemo(() => {
    return blogPosts.filter((post) => {
      // In English mode, only show posts that have English translations
      if (!isRTL && !blogPostsEn[post.slug]) {
        return false;
      }

      const translatedPost = getTranslatedPost(post);

      // Category filtering
      const matchesCategory = isRTL
        ? selectedCategory === "הכל" || post.category === selectedCategory
        : selectedCategory === "All" || translatedPost.category === selectedCategory;

      // Search filtering (use translated fields when in English)
      const searchIn = isRTL ? post : translatedPost;
      const q = searchQuery.trim();
      const matchesSearch =
        q.length === 0 ||
        searchIn.title.includes(q) ||
        searchIn.excerpt.includes(q) ||
        searchIn.keywords.some((k) => k.includes(q));

      return matchesCategory && matchesSearch;
    });
  }, [selectedCategory, searchQuery, isRTL]);

  const displayedPosts = filteredPosts.slice(0, visiblePosts);
  const hasMorePosts = visiblePosts < filteredPosts.length;

  const loadMore = () => {
    setVisiblePosts(prev => prev + 6);
  };


  // Schema for blog listing page
  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: t('meta.title'),
    description: t('meta.description'),
    url: "https://adam-safety.com/blog",
    publisher: {
      "@type": "Organization",
      name: "Adam Safety",
    },
    blogPost: blogPosts.slice(0, 10).map((post) => {
      const translated = getTranslatedPost(post);
      return {
      "@type": "BlogPosting",
      headline: translated.title,
      description: translated.excerpt,
      datePublished: post.date,
      url: `https://adam-safety.com/blog/${post.slug}`,
      };
    }),
  };

  return (
    <Layout>
      <Helmet>
        <title>{t('meta.title')}</title>
        <meta name="description" content={t('meta.description')} />
        <meta
          name="keywords"
          content={
            isRTL
              ? "בטיחות בעבודה, הדרכות בטיחות, סקר סיכונים, ממונה בטיחות, תקנות בטיחות, ISO 45001"
              : "workplace safety, safety training, risk assessment, safety officer, safety regulations, ISO 45001"
          }
        />
        <link rel="canonical" href="https://adam-safety.com/blog" />
        <meta property="og:title" content={t('meta.title')} />
        <meta property="og:description" content={t('meta.description')} />
        <meta property="og:url" content="https://adam-safety.com/blog" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(blogSchema)}</script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[40vh] flex items-center justify-center overflow-hidden bg-primary">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-accent/20" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-72 h-72 bg-accent rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-accent/50 rounded-full blur-3xl" />
        </div>
        
        <div className="container relative z-10 px-4 py-24 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-block px-4 py-2 rounded-full bg-accent/20 text-accent text-sm font-semibold mb-6">
              {t('hero.badge')}
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
              {t('hero.title')}
            </h1>
            <p className="text-xl text-primary-foreground/80 max-w-2xl mx-auto">
              {isRTL 
                ? `מאמרים, מדריכים וטיפים מעולם הבטיחות בעבודה - ${blogPosts.length} ${t('hero.articleCount')}`
                : `Articles, guides and tips from the world of workplace safety - ${blogPosts.length} ${t('hero.articleCount')}`
              }
            </p>
          </motion.div>
        </div>
      </section>

      {/* Search & Filter with Animation */}
      <section className="py-8 bg-background border-b border-border sticky top-16 z-30">
        <div className="container px-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative w-full md:w-80">
              <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground`} />
              <Input 
                placeholder={t('search.placeholder')} 
                className={isRTL ? 'pr-10' : 'pl-10'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {translatedCategories.map((category, index) => (
                <motion.button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 ${
                    selectedCategory === category 
                      ? "bg-accent text-accent-foreground shadow-md" 
                      : "bg-secondary text-secondary-foreground hover:bg-accent/20"
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {category}
                </motion.button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Blog Grid with Animated Filter */}
      <section className="py-20 bg-background">
        <div className="container px-4">
          <AnimatePresence mode="popLayout">
            <motion.div layout className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {displayedPosts.map((post, index) => {
                const translatedPost = getTranslatedPost(post);
                return (
                  <motion.article
                    key={post.id}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    <Link 
                      to={`/blog/${post.slug}`}
                      className="group block bg-card rounded-2xl overflow-hidden border border-border hover:border-accent/30 hover:shadow-xl transition-all duration-300 h-full"
                    >
                      <div className="aspect-video overflow-hidden relative">
                        <img 
                          src={post.image} 
                          alt={translatedPost.imageAlt} 
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          onError={(e) => {
                            const target = e.currentTarget;
                            // Prevent infinite loop by checking if already using fallback
                            if (!target.dataset.fallback) {
                              target.dataset.fallback = "true";
                              target.src = getFallbackImageUrl(post.id, post.category);
                            }
                          }}
                        />
                        
                        {/* Badges */}
                        <div className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} flex gap-2`}>
                          {isNewPost(post.date) && (
                            <span className="px-2.5 py-1 text-xs font-bold rounded-full bg-accent text-accent-foreground flex items-center gap-1">
                              <Sparkles className="w-3 h-3" />
                              {t('post.new')}
                            </span>
                          )}
                          {post.featured && (
                            <span className="px-2.5 py-1 text-xs font-bold rounded-full bg-primary text-primary-foreground">
                              {t('post.featured')}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="p-6">
                        <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-accent/10 text-accent mb-4">
                          {translatedPost.category}
                        </span>
                        <h2 className="font-heading text-xl font-bold text-foreground mb-3 line-clamp-2 group-hover:text-accent transition-colors">
                          {translatedPost.title}
                        </h2>
                        <p className="text-muted-foreground text-sm mb-4 line-clamp-2">{translatedPost.excerpt}</p>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(post.date).toLocaleDateString(isRTL ? 'he-IL' : 'en-US')}
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {translatedPost.readTime}
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.article>
                );
              })}
            </motion.div>
          </AnimatePresence>
          
          {filteredPosts.length === 0 && (
            <div className="text-center py-16">
              <p className="text-muted-foreground text-lg">{t('search.noResults')}</p>
            </div>
          )}
          
          {hasMorePosts && (
            <div className="text-center mt-12">
              <Button variant="outline" size="lg" className="gap-2" onClick={loadMore}>
                {t('post.loadMore')}
                <ArrowIcon className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 bg-primary">
        <div className="container px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-foreground mb-6">
              {t('newsletter.title')}
            </h2>
            <p className="text-lg text-primary-foreground/80 mb-8 max-w-xl mx-auto">
              {t('newsletter.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
              <Input 
                placeholder={t('newsletter.placeholder')} 
                className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/50" 
              />
              <Button variant="cta">{t('newsletter.subscribe')}</Button>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
